import { useMemo } from "react";
import { useNavigate } from "react-router";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import MetaInfoList from "components/MetaInfoList";
import SpacePath from "components/SpacePath";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import TabWithBadge from "ds/components/Tab/WithBadge";
import TabWithCounter from "ds/components/Tab/WithCounter";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageContext } from "hooks/useAnalytics/pages/context";
import useTypedContext from "hooks/useTypedContext";
import { ConfigType } from "types/generated";
import { openContextUpdateDrawer } from "views/Account/Contexts/UpdateDrawer";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import useCanDeleteContext from "shared/Context/access/useCanDeleteContext";
import useCanUpdateContext from "shared/Context/access/useCanUpdateContext";
import ContextsDeleteDropdownItem from "views/Account/Contexts/DeleteDropdownItem";

import { CONTEXT_ANALYTICS_VERSION } from "../constants";
import { ContextContext } from "../Context";
import { showContextDetailsDrawer } from "../DetailsDrawer";
import { ContextHooks } from "../types";

const ContextHeader = () => {
  const { context, contextUrl } = useTypedContext(ContextContext);
  const canDeleteContext = useCanDeleteContext()(context);
  const canUpdateContext = useCanUpdateContext()(context);
  const navigate = useNavigate();

  const {
    attachedStacks,
    hasAttachedStacks,
    id,
    name,
    createdAt,
    spaceDetails,
    config,
    hooks,
    labels,
  } = context;

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageContext.Context,
    defaultCallbackTrackProperties: { version: CONTEXT_ANALYTICS_VERSION },
  });

  const variablesCount = config.filter(
    (item) => item.type === ConfigType.EnvironmentVariable
  ).length;

  const mountedFilesCount = config.filter((item) => item.type === ConfigType.FileMount).length;
  const hasHooksDefined = useMemo(() => {
    return Object.keys(hooks).some((item) => {
      const key = item as keyof ContextHooks;
      if (key === "__typename") return;
      const value = hooks[key];

      return value && value.length > 0;
    });
  }, [hooks]);

  const onDeleteSuccess = () => {
    trackSegmentAnalyticsEvent("Delete", {
      labels: labels,
      space: spaceDetails.name,
      name: name,
    });

    navigate("/contexts");
  };

  const showDetailsDrawer = () => {
    showContextDetailsDrawer({ context });
  };

  const showEditDrawer = () => {
    openContextUpdateDrawer({ context, refetchQuery: "GetContext" });
  };

  return (
    <ViewHeader>
      <ViewHeaderWrapper direction="row" justify="between" fullWidth>
        <ViewHeaderWrapper direction="row" align="center">
          <ViewHeaderTitle>{name}</ViewHeaderTitle>
        </ViewHeaderWrapper>

        <ViewHeaderWrapper direction="row" align="end" shrink="0">
          <Button variant="secondary" onPress={showDetailsDrawer}>
            Details
          </Button>

          <DropdownMenuEllipsis tooltip="Context actions" dotsSize="medium">
            <CopyFieldDropdownMenuItem title="Copy context ID" value={id} />

            {(canUpdateContext.result || canUpdateContext.message) && (
              <DropdownMenuItem
                onAction={showEditDrawer}
                isDisabled={!canUpdateContext.result}
                tooltip={canUpdateContext.message}
                tooltipPlacement="left"
              >
                Edit
              </DropdownMenuItem>
            )}

            {(canDeleteContext.result || canDeleteContext.message) && (
              <ContextsDeleteDropdownItem
                hasAttachedEntities={hasAttachedStacks}
                name={name}
                id={id}
                onSuccess={onDeleteSuccess}
                isDisabled={!canDeleteContext.result}
                tooltip={canDeleteContext.message}
              />
            )}
          </DropdownMenuEllipsis>
        </ViewHeaderWrapper>
      </ViewHeaderWrapper>
      <ViewHeaderScrollCollapse>
        <MetaInfoList>
          {spaceDetails && (
            <SpacePath variant="meta" spaceId={spaceDetails.id} fallbackName={spaceDetails.name} />
          )}
          {createdAt && <TimestampMetaInfo timestamp={createdAt} />}
        </MetaInfoList>
      </ViewHeaderScrollCollapse>

      <ViewHeaderNavigation>
        <TabWithCounter end to={contextUrl} count={variablesCount} label="Variables" />
        <TabWithCounter
          to={`${contextUrl}/files`}
          count={mountedFilesCount}
          label="Mounted files"
        />
        <TabWithBadge
          badgeValue={hasHooksDefined ? "Defined" : "None"}
          to={`${contextUrl}/hooks`}
          label="Hooks"
        />
        <TabWithCounter
          to={`${contextUrl}/used-by`}
          count={attachedStacks.length}
          label="Used by"
        />
      </ViewHeaderNavigation>
    </ViewHeader>
  );
};

export default ContextHeader;
